import type { ImportDisplayStatus, InvoiceImportCandidate, ParsedInvoiceDraft } from "./types";

export function draftNeedsReview(draft: ParsedInvoiceDraft): boolean {
  if (draft.parseWarnings.length > 0) return true;
  if (!draft.clientName?.trim()) return true;
  if (!draft.invoiceNumber?.trim()) return true;
  return draft.lines.some((line) => line.needsReview);
}

export function resolveImportDisplayStatus(item: InvoiceImportCandidate): ImportDisplayStatus {
  if (item.importedEventId) return "imported";

  switch (item.parseStatus) {
    case "waiting":
      return "waiting";
    case "parsing":
      return "parsing";
    case "failed":
      return "failed";
    case "parsed":
      if (item.markedReady) return "ready";
      if (item.draft && draftNeedsReview(item.draft)) return "needs_review";
      return "parsed";
    default:
      return "waiting";
  }
}

export function summarizeImportQueue(items: InvoiceImportCandidate[]) {
  const counts: Record<ImportDisplayStatus, number> = {
    waiting: 0,
    parsing: 0,
    parsed: 0,
    needs_review: 0,
    ready: 0,
    failed: 0,
    imported: 0,
  };
  for (const item of items) {
    counts[resolveImportDisplayStatus(item)] += 1;
  }
  return counts;
}

export function formatCandidateClient(draft?: ParsedInvoiceDraft): string {
  return draft?.clientName?.trim() || "—";
}

export function formatCandidateEvent(draft?: ParsedInvoiceDraft): string {
  if (draft?.projectName?.trim()) return draft.projectName.trim();
  if (draft?.eventType?.trim() && draft?.eventDate?.trim()) {
    return `${draft.eventType} · ${draft.eventDate}`;
  }
  return draft?.eventDate?.trim() || "—";
}

export function formatCandidateInvoiceNumber(draft?: ParsedInvoiceDraft): string {
  return draft?.invoiceNumber?.trim() || "—";
}

export function formatImportedAt(iso?: string): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
